import React, { useState, useEffect, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Dropdown, Modal, Button } from "react-bootstrap";
import { Formik, Field, Form as FormikForm, ErrorMessage } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import Spinner from "../components/Common/Spinner";
import Pagination from "../components/Pagination";
import { createUserRole, deleteUserRole, getUserRole,updateUserRole } from "../../services/UserServices/UserService";
import { useSelector } from "react-redux";
// Static mock data for roles to use until APIs are ready
const MOCK_ROLES = [
  { _id: "1", roleName: "Super Admin", description: "Full system access across all modules.", totalUsers: 2, status: "Active", createdAt: "2026-01-15" },
  { _id: "2", roleName: "Listing Moderator", description: "Can view, approve, reject, or delete property listings.", totalUsers: 5, status: "Active", createdAt: "2026-02-10" },
  { _id: "3", roleName: "Support Agent", description: "Read-only access to listings and managing user reports.", totalUsers: 8, status: "Active", createdAt: "2026-03-01" },
  { _id: "4", roleName: "Content Manager", description: "Handles multi-language content sections and blogs.", totalUsers: 3, status: "Inactive", createdAt: "2026-04-12" },
];

const Role = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  
  // Filter & Search states
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const location = useLocation();
  const navigate = useNavigate();
  // Pagination states
  const [currentPage, setCurrentPage] = useState(
    location.state?.currentPage ?? 0
  );
  const [itemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(MOCK_ROLES.length);

  // Confirmation modal state
  const [confirmState, setConfirmState] = useState({
    show: false,
    action: null,
    id: null,
    status: null,
    message: "",
  });

  const [submit, setSubmit] = useState(false);
 


  const svgDots = (
    <svg width="20px" height="20px" viewBox="0 0 24 24" version="1.1">
      <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
        <rect x="0" y="0" width="24" height="24"></rect>
        <circle fill="#000000" cx="12" cy="5" r="2"></circle>
        <circle fill="#000000" cx="12" cy="12" r="2"></circle>
        <circle fill="#000000" cx="12" cy="19" r="2"></circle>
      </g>
    </svg>
  );

  // Mocking data fetching logic
  const fetchRoles = useCallback(async () => {
    setLoading(true);
    try {
       const res = await getUserRole(currentPage,10);
      setRoles(res?.data?.data?.roles);
      setTotalItems(res?.data?.data?.total);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load roles");
    } finally {
      setLoading(false);
    }
  }, [currentPage]);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  useEffect(() => {
    if (currentPage !== undefined) {
      navigate(".", {
        replace: true,
        state: { ...location.state, currentPage },
      });
    }
  }, [currentPage]);

  // Reset page index on filter updates
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    setCurrentPage(0);
  }, [searchText, statusFilter]);

  const handleClearFilters = () => {
    setSearchText("");
    setStatusFilter("");
  };

  const isFilterActive = searchText || statusFilter;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  // Open Edit/Create modal
  const openModal = (role = null) => {
    setSelectedRole(role);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedRole(null);
  };

const crudPermissionSchema = Yup.object().shape({
  isAdd: Yup.boolean().default(false),
  isUpdate: Yup.boolean().default(false),
  isDelete: Yup.boolean().default(false),
  isRead: Yup.boolean().default(false),
});
  const RoleSchema = Yup.object().shape({
   name: Yup.string()
    .trim()
    .min(3, 'Role name must be at least 3 characters')
    .max(50, 'Role name cannot exceed 50 characters')
    .required('Role name is required'),
  isDashboard: Yup.boolean().default(false),
  waitListManagement: crudPermissionSchema,
  misManagement: crudPermissionSchema,
  userManagement: crudPermissionSchema,
  deleteAccountManagement: crudPermissionSchema,
  contactUsManagement: crudPermissionSchema,
  adsManagement:crudPermissionSchema,
  notificationManagement:crudPermissionSchema,
  legalPagesManagement:crudPermissionSchema,
  });

  // Handle Form Submission
  const handleSubmit = async (values, { resetForm }) => {
    setSubmit(true);
    try {
      if (selectedRole) {
       const payload ={...values,id:selectedRole._id}
        await updateUserRole(payload);
          toast.success("Role updated successfully ");
      } else {
        await createUserRole(values);
        toast.success("New role added successfully  )");
        resetForm();
      }
      handleCloseModal();
      fetchRoles();
    } catch (error) {
      toast.error("Failed to save role");
    } finally {
      setSubmit(false);
    }
  };

  // Trigger Action Confirmation Modals
  const triggerConfirm = (action, id, status = null) => {
    let message = "";
    if (action === "delete") {
      message = "Are you sure you want to delete this role? This action cannot be undone.";
    } else if (action === "status") {
      message = `Are you sure you want to change this role's status to ${status.toLowerCase()}?`;
    }
    setConfirmState({ show: true, action, id, status, message });
  };

  const handleDeleteRole = async (id) => {
    try {
   
      await deleteUserRole(id);
      toast.success("Role deleted successfully ");
      setConfirmState({ show: false, action: null, id: null, status: null, message: "" });
      fetchRoles();
    } catch (error) {
      toast.error("Failed to delete role");
    } finally {
      setSubmit(false);
    }
  };
  const confirmActionHandler = () => {
    const { action, status } = confirmState;
    if (action === "delete") {
       handleDeleteRole(confirmState.id);
    } else if (action === "status") {
      toast.success(`Role status updated to ${status} (Static)`);
    }
    setConfirmState({ show: false, action: null, id: null, status: null, message: "" });
  };

  return (
    <div className="container mt-4">
      {/* Header View */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h1 style={{ color: "#E5484D" }}>Roles</h1>
        <button
          onClick={() => openModal(null)}
          className="btn btn-sm text-white d-flex align-items-center gap-2 shadow-sm px-3 py-2"
          style={{ backgroundColor: "#E5484D", borderRadius: "0.5rem", border: "none" }}
        >
          Add New Role
        </button>
      </div>

      {/* Filters/Controls Layout */}
      {/* <div className="pb-2 d-flex flex-wrap justify-content-between gap-2 align-items-center mb-4">
        <div className="flex-grow-1" style={{ minWidth: "250px" }}>
          <label className="form-label mb-1" style={{ fontSize: "12px" }}>Search Roles</label>
          <input
            type="text"
            className="form-control"
            placeholder="Search by role name or details..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ borderRadius: "0.5rem", borderColor: "#E5484D" }}
          />
        </div>

        <div>
          <label className="form-label mb-1" style={{ fontSize: "12px" }}>Status</label>
          <select
            className="form-control"
            value={statusFilter}
            style={{ borderRadius: "0.5rem", borderColor: "#E5484D", minWidth: "150px" }}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
        </div>

        {isFilterActive && (
          <div className="d-flex flex-column">
            <label className="form-label mb-1" style={{ fontSize: "12px" }}>&nbsp;</label>
            <button
              className="btn btn-outline-danger"
              onClick={handleClearFilters}
              style={{ borderRadius: "0.5rem" }}
            >
              Clear
            </button>
          </div>
        )}
      </div> */}

      {/* Main Roles Table */}
      {loading ? (
        <Spinner />
      ) : (
        <div className="table-responsive">
          <table className="table table-striped align-middle shadow-sm">
            <thead className="table-light">
              <tr>
                <th>Sr No.</th>
                <th>Role Name</th>
                
                <th>Created At</th>
                <th style={{ textAlign: "center" }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {roles.length > 0 ? (
                roles.map((role, index) => (
                  <tr key={role._id || index}>
                    <td>{currentPage * itemsPerPage + index + 1}</td>
                    <td className="fw-semibold">{role.name.charAt(0).toUpperCase() + role.name.slice(1)}</td>
                    
                   
                    <td>{new Date(role.createdAt).toLocaleDateString()}</td>
                    <td style={{ textAlign: "center" }}>
                      <Dropdown>
                        <Dropdown.Toggle variant="light" style={{ padding: "4px 8px" }}>
                          {svgDots}
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item onClick={() => openModal(role)}>
                            Edit Role
                          </Dropdown.Item>
                          {/* <Dropdown.Item
                            onClick={() =>
                              triggerConfirm(
                                "status",
                                role._id,
                                role.status === "Active" ? "Inactive" : "Active"
                              )
                            }
                          >
                            Mark As {role.status === "Active" ? "Inactive" : "Active"}
                          </Dropdown.Item> */}
                          <Dropdown.Item onClick={() => triggerConfirm("delete", role._id)}>
                            Delete
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="text-center">No structural roles found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Table Footer / Pagination */}
      <div className="card-footer clearfix mt-3">
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-center">
          <div className="d-flex align-items-center">
            {totalPages > 0 && (
              <Pagination
                pageCount={totalPages}
                pageValue={currentPage}
                setPage={setCurrentPage}
              />
            )}
          </div>
        </div>
      </div>

      {/* Add / Edit Role Modal Context */}
  <Modal size="lg" show={showModal} onHide={handleCloseModal} centered>
  <Modal.Header closeButton style={{ backgroundColor: "#FFE4E6" }} >
    <Modal.Title style={{color:"#E5484D"}}> {selectedRole ? "Edit Role" : "Create Role"}</Modal.Title>
  </Modal.Header>
  <Formik
    initialValues={{
      name: selectedRole ? selectedRole.name : "", 
      isDashboard: selectedRole ? selectedRole.isDashboard : false, // Booleans work best for checkboxes
      waitListManagement: selectedRole ? selectedRole.waitListManagement : {
        isAdd: false,
        isUpdate: false,
        isDelete: false,
        isRead: false,
      },
      misManagement: selectedRole ? selectedRole.misManagement : {
        isAdd: false,
        isUpdate: false,
        isDelete: false,
        isRead: false,
      },
      userManagement: selectedRole ? selectedRole.userManagement : {
        isAdd: false,
        isUpdate: false,
        isDelete: false,
        isRead: false,
      },
      deleteAccountManagement: selectedRole ? selectedRole.deleteAccountManagement : {
       isAdd: false,
        isUpdate: false,
        isDelete: false,
        isRead: false,
      },
      adsManagement: selectedRole ? selectedRole.adsManagement : {
        isAdd: false,
        isUpdate: false,
        isDelete: false,
        isRead: false,
      },
      contactUsManagement: selectedRole ? selectedRole.contactUsManagement : {
      isAdd: false,
        isUpdate: false,
        isDelete: false,
        isRead: false,
      },
      notificationManagement: selectedRole ? selectedRole.notificationManagement : {
       isAdd: false,
        isUpdate: false,
        isDelete: false,
        isRead: false,
      },
      legalPagesManagement: selectedRole ? selectedRole.legalPagesManagement : {
      isAdd: false,
        isUpdate: false,
        isDelete: false,
        isRead: false,
      },
        reportManagement: selectedRole ? selectedRole.reportManagement : {
      isAdd: false,
        isUpdate: false,
        isDelete: false,
        isRead: false,
      },
         blockedUserManagement: selectedRole ? selectedRole.blockedUserManagement : {
      isAdd: false,
        isUpdate: false,
        isDelete: false,
        isRead: false,
      },
          safetyCheckManagement: selectedRole ? selectedRole.safetyCheckManagement : {
      isAdd: false,
        isUpdate: false,
        isDelete: false,
        isRead: false,
      },

    }}
    enableReinitialize
    validationSchema={RoleSchema}
    onSubmit={handleSubmit}
  >
    {({ errors, touched ,values }) => (
      <FormikForm>
        <Modal.Body>
          {/* Role Name */}
          <div className="mb-3">
            <label className="form-label fw-bold " >Role Name</label>
            <Field
              name="name" // Fixed: Changed from roleName to name
              placeholder="e.g., Listing Moderator"
              className={`form-control custom-input ${errors.name && touched.name ? "input-error" : ""}`}
            />
            <ErrorMessage name="name" component="div" className="text-danger small mt-1" />
          </div>

          {/* Description */}
    

          {/* Dashboard Access */}
          <div className="mb-3">
            <label className="form-label fw-bold d-block">General Access</label>
            <div style={{backgroundColor:"#FFE4E7"}} className="p-3 border rounded-3 text-black  ">
              <label className="d-flex align-items-center gap-2 m-0 cursor-pointer">
                <Field type="checkbox" name="isDashboard" />
                <span>Dashboard Access</span>
              </label>
            </div>
          </div>

          {/* Module Permissions Matrix */}
          <div className="mb-2">
            <label className="form-label fw-bold d-block">Module Permissions</label>
            <div  style={{backgroundColor:"#FFE4E7"}} className="table-responsive border rounded-3  p-2">
              <table className="table table-borderless align-middle m-0  text-black">
                <thead>
                  <tr>
                    <th>Module</th>
                    <th className="text-center">Create (Add)</th>
                    <th className="text-center">Read (View)</th>
                    <th className="text-center">Update (Edit)</th>
                    <th className="text-center">Delete</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Waitlist Management */}
                  <tr>
                    <td>Waitlist Management</td>
                    {/* <td className="text-center"><Field type="checkbox" name="waitListManagement.isAdd" /></td> */}
                     <td className="text-center"> </td>
                    <td className="text-center"><Field type="checkbox" name="waitListManagement.isRead" /></td>
                    {/* <td className="text-center"><Field type="checkbox" name="waitListManagement.isUpdate" /></td>
                    <td className="text-center"><Field type="checkbox" name="waitListManagement.isDelete" /></td> */}
                  </tr>

                  {/* MIS Management */}
                  <tr>
                    <td>MIS Management</td>
                    {/* <td className="text-center"><Field type="checkbox" name="misManagement.isAdd" /></td> */}
                     <td className="text-center"> </td>

                    <td className="text-center"><Field type="checkbox" name="misManagement.isRead" /></td>
                    {values?.misManagement?.isRead ?<><td className="text-center"><Field type="checkbox" name="misManagement.isUpdate" /></td>
                    <td className="text-center"><Field type="checkbox" name="misManagement.isDelete" /></td></> :"" }
                    
                  </tr>

                  {/* User Management */}
                  <tr>
                    <td>New Requests</td>
                    {/* <td className="text-center"><Field type="checkbox" name="userManagement.isAdd" /></td> */}
                      <td className="text-center"> </td>
                    <td className="text-center"><Field type="checkbox" name="userManagement.isRead" /></td>
                    {/* <td className="text-center"><Field type="checkbox" name="userManagement.isUpdate" /></td>
                    <td className="text-center"><Field type="checkbox" name="userManagement.isDelete" /></td> */}
                  </tr>

                  {/* Delete Account Management */}
                  <tr>
                    <td>Delete Account Management</td>
                    {/* <td className="text-center"><Field type="checkbox" name="deleteAccountManagement.isAdd" /></td> */}
                        <td className="text-center"> </td>
                    <td className="text-center"><Field type="checkbox" name="deleteAccountManagement.isRead" /></td>
                    {/* <td className="text-center"><Field type="checkbox" name="deleteAccountManagement.isUpdate" /></td>
                    <td className="text-center"><Field type="checkbox" name="deleteAccountManagement.isDelete" /></td> */}
                  </tr>

                  {/* Ads Management */}
                  <tr>
                    <td>Ads Management</td>
                    {/* <td className="text-center"><Field type="checkbox" name="adsManagement.isAdd" /></td> */}
                     <td className="text-center"> </td>
                   
                    <td className="text-center"><Field type="checkbox" name="adsManagement.isRead" /></td>
                      {values?.adsManagement?.isRead ? <>  <td className="text-center"><Field type="checkbox" name="adsManagement.isUpdate" /></td>
                    <td className="text-center"><Field type="checkbox" name="adsManagement.isDelete" /></td></>:""}
                  
                  </tr>

                  {/* Contact Us Management */}
                  <tr>
                    <td>Contact Us Management</td>
                    {/* <td className="text-center"><Field type="checkbox" name="contactUsManagement.isAdd" /></td> */}
                     <td className="text-center"> </td>
                    <td className="text-center"><Field type="checkbox" name="contactUsManagement.isRead" /></td>
                 {values.contactUsManagement.isRead ?  <td className="text-center"><Field type="checkbox" name="contactUsManagement.isUpdate" /></td>:""}

                    
                    {/* <td className="text-center"><Field type="checkbox" name="contactUsManagement.isDelete" /></td> */}
                  </tr>


                  {/* Notification Management */}
                  <tr>
                    <td>Notification Management</td>
                    {values.notificationManagement.isRead? <td className="text-center"><Field type="checkbox" name="notificationManagement.isAdd" /></td>: <td></td>}
                   
                    <td className="text-center"><Field type="checkbox" name="notificationManagement.isRead" /></td>
                    {/* <td className="text-center"><Field type="checkbox" name="notificationManagement.isUpdate" /></td> */}
                    {/* <td className="text-center"><Field type="checkbox" name="notificationManagement.isDelete" /></td> */}
                  </tr>

                   {/* Notification Management */}
                  <tr>
                    <td>Reported Users</td>
                    {/* <td className="text-center"><Field type="checkbox" name="notificationManagement.isAdd" /></td> */}
                    <td className="text-center"> </td>
                    <td className="text-center"><Field type="checkbox" name="reportManagement.isRead" /></td>
            
                  </tr>

  <tr>
                    <td>Blocked Users</td>
                    {/* <td className="text-center"><Field type="checkbox" name="notificationManagement.isAdd" /></td> */}
                    <td className="text-center"> </td>
                    <td className="text-center"><Field type="checkbox" name="blockedUserManagement.isRead" /></td>
            
                  </tr>
                    <tr>
                    <td>Safety Check</td>
                    {/* <td className="text-center"><Field type="checkbox" name="notificationManagement.isAdd" /></td> */}
                    <td className="text-center"> </td>
                    <td className="text-center"><Field type="checkbox" name="safetyCheckManagement.isRead" /></td>
            
                  </tr>



                  {/* Legal Pages Management */}
                  <tr>
                    <td>Legal Pages Management</td>
                    {/* <td className="text-center"><Field type="checkbox" name="legalPagesManagement.isAdd" /></td> */}
                     <td className="text-center"> </td>
                    <td className="text-center"><Field type="checkbox" name="legalPagesManagement.isRead" /></td>
                    {values?.legalPagesManagement?.isRead ?  <td className="text-center"><Field type="checkbox" name="legalPagesManagement.isUpdate" /></td>:""}
                   
                    {/* <td className="text-center"><Field type="checkbox" name="legalPagesManagement.isDelete" /></td> */}
                  </tr>


                </tbody>
              </table>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer style={{ backgroundColor: "#FFE4E6" }}>
          <Button variant="secondary" onClick={handleCloseModal}>Cancel</Button>
          <Button type="submit" style={{ backgroundColor: "#E5484D", borderColor: "#E5484D", color: "white" }} disabled={submit}>
            {submit ? "Saving..." : "Save Changes"}
          </Button>
        </Modal.Footer>
      </FormikForm>
    )}
  </Formik>
</Modal>

      {/* Global Confirmation Modal */}
      <Modal show={confirmState.show} onHide={() => setConfirmState({ ...confirmState, show: false })} centered>
        <Modal.Header closeButton>
          <Modal.Title></Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0 fs-5">{confirmState.message}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setConfirmState({ ...confirmState, show: false })}>
            Cancel
          </Button>
          <Button
            variant={confirmState.action === "delete" ? "danger" : "primary"}
            style={confirmState.action !== "delete" ? { backgroundColor: "#E5484D", borderColor: "#E5484D" } : {}}
            onClick={confirmActionHandler}
          >
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Role;